// src/pages/admin/AdminDistribusiPage.jsx

import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  Table,
  Loader,
  Center,
  Alert,
  Title,
  ScrollArea,
  Badge,
  Group,
  Button,
  Text,
  ThemeIcon,
  Paper,
  Select // <-- 1. Import Select
} from "@mantine/core";
import { 
  IconInfoCircle, 
  IconBuilding, 
  IconChefHat,
  IconRefresh,
  IconFilter // <-- 2. Import Icon Filter
} from "@tabler/icons-react";

// Helper Warna Status
const statusColors = {
  diterima: 'green',
  dikirim: 'blue',
  disiapkan: 'yellow',
  dibatalkan: 'red'
};

export default function AdminDistribusiPage() {
  const [distribusiList, setDistribusiList] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- 3. State untuk Filter Sekolah ---
  const [filterSekolah, setFilterSekolah] = useState(null);

  const fetchDistribusi = async () => {
    setLoading(true);
    try {
      const response = await api.get("/admin/distribusi");
      
      let hasilData = [];
      if (Array.isArray(response.data)) {
        hasilData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        hasilData = response.data.data;
      } else {
        hasilData = [];
      }

      setDistribusiList(hasilData);
      setError(null);

    } catch (err) {
      console.error(err);
      setError("Gagal mengambil data distribusi. Pastikan server berjalan.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistribusi();
  }, []);

  // --- 4. LOGIKA FILTERING ---
  
  // A. Ambil daftar sekolah unik dari data yang ada untuk opsi Dropdown
  const schoolOptions = Array.from(
    new Set(
      distribusiList
        .map((item) => item.sekolah?.nama_sekolah) // Ambil nama sekolah
        .filter((nama) => nama) // Hapus yang null/undefined
    )
  ).map((nama) => ({ value: nama, label: nama }));

  // B. Filter Data List berdasarkan pilihan dropdown
  const filteredList = distribusiList.filter((item) => {
    if (!filterSekolah) return true; // Jika tidak ada filter, tampilkan semua
    return item.sekolah?.nama_sekolah === filterSekolah;
  });


  // --- RENDER LOADING & ERROR ---
  if (loading) return <Center h={300}><Loader size="lg" /></Center>;

  if (error) {
    return (
      <Alert icon={<IconInfoCircle />} title="Error" color="red" mt="md">
        {error}
        <br />
        <Button variant="outline" color="red" size="xs" mt="sm" onClick={fetchDistribusi}>
            Coba Lagi
        </Button>
      </Alert>
    );
  }

  // --- RENDER BARIS TABEL (Gunakan filteredList) ---
  const rows = Array.isArray(filteredList) && filteredList.length > 0 ? (
    filteredList.map((item) => (
      <Table.Tr key={item.id}>
        {/* Kolom Tanggal */}
        <Table.Td>
          <Text fz="sm" fw={500}>
              {new Date(item.tanggal_distribusi).toLocaleDateString('id-ID', {
                  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
              })}
          </Text>
        </Table.Td>
        
        {/* Kolom Sekolah */}
        <Table.Td>
          <Group gap="sm">
              <ThemeIcon color="blue" variant="light" size="lg" radius="xl">
                <IconBuilding size={18}/>
              </ThemeIcon>
              <div>
                  <Text fz="sm" fw={500}>{item.sekolah?.nama_sekolah || <span style={{color:'red'}}>Sekolah Dihapus</span>}</Text>
                  <Text fz="xs" c="dimmed">NPSN: {item.sekolah?.npsn || "-"}</Text>
              </div>
          </Group>
        </Table.Td>

        {/* Kolom Menu & Penyedia */}
        <Table.Td>
          <Group gap="sm">
              <ThemeIcon color="orange" variant="light" size="lg" radius="xl">
                <IconChefHat size={18}/>
              </ThemeIcon>
              <div>
                  <Text fz="sm" fw={500}>{item.menu?.nama_menu || <span style={{color:'red'}}>Menu Dihapus</span>}</Text>
                  <Text fz="xs" c="dimmed">{item.penyedia?.nama_perusahaan || "Penyedia Dihapus"}</Text>
              </div>
          </Group>
        </Table.Td>

        {/* Kolom Jumlah */}
        <Table.Td>
          <Badge variant="outline" color="gray" size="lg">
             {item.jumlah_paket} Porsi
          </Badge>
        </Table.Td>

        {/* Kolom Status */}
        <Table.Td>
          <Badge 
              color={statusColors[item.status] || 'gray'}
              variant="light"
              size="md"
          >
              {item.status ? item.status.toUpperCase() : 'UNKNOWN'}
          </Badge>
        </Table.Td>
      </Table.Tr>
    ))
  ) : (
    <Table.Tr>
        <Table.Td colSpan={5}>
            <Center p="xl" c="dimmed">
                Tidak ada data distribusi yang sesuai filter.
            </Center>
        </Table.Td>
    </Table.Tr>
  );

  // --- RENDER UTAMA ---
  return (
    <>
      <Group justify="space-between" mb="lg">
        <div>
           <Title order={2}>Monitoring Distribusi</Title>
           <Text c="dimmed" size="sm">Memantau pengiriman dari Penyedia ke Sekolah</Text>
        </div>
        <Button variant="light" leftSection={<IconRefresh size={16}/>} onClick={fetchDistribusi}>
            Refresh Data
        </Button>
      </Group>

      {/* --- 5. UI FILTER --- */}
      <Paper p="md" mb="md" withBorder radius="md" bg="gray.0">
         <Group>
            <Select 
                placeholder="Filter Sekolah Tujuan"
                leftSection={<IconFilter size={16} />}
                data={schoolOptions}
                value={filterSekolah}
                onChange={setFilterSekolah}
                clearable
                searchable
                nothingFoundMessage="Sekolah tidak ditemukan"
                style={{ width: 300 }}
            />
            {filterSekolah && (
                <Text size="sm" c="dimmed">
                    Menampilkan distribusi untuk: <b>{filterSekolah}</b>
                </Text>
            )}
         </Group>
      </Paper>

      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
          <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead bg="gray.0">
              <Table.Tr>
                <Table.Th>Tanggal</Table.Th>
                <Table.Th>Sekolah Tujuan</Table.Th>
                <Table.Th>Menu & Penyedia</Table.Th>
                <Table.Th>Jumlah</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
               {rows}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Paper>
    </>
  );
}